import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Download, Lock, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { mediaSrc } from "@/components/HeroVideo";

export default function ProposalPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/proposals/${id}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [id]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/proposals/${id}/deposit`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  if (err) return <div className="px-6 py-28 text-center font-display text-3xl text-white" data-testid="proposal-missing">Proposal not found.</div>;
  if (!p) return <div className="flex justify-center py-28"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>;
  return (
    <div className="mx-auto max-w-3xl px-6 py-16 md:py-24" data-testid="proposal-page">
      <p className="eyebrow">Proposal · {p.reference}</p>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl">{p.lane_name}</h1>
      <p className="mt-3 text-neutral-400">Prepared for {p.company || p.name}</p>
      <div className="glass mt-10 divide-y divide-white/10">
        {p.items.map((it, i) => (
          <div key={i} className="flex items-center justify-between gap-4 px-5 py-4 text-sm" data-testid={`proposal-item-${i}`}>
            <span className="text-neutral-200">{it.description} <span className="text-neutral-500">× {it.qty}</span></span>
            <span className="font-cond text-base">${(it.qty * it.unit).toLocaleString()}</span>
          </div>
        ))}
        <div className="flex items-center justify-between px-5 py-5"><span className="font-cond uppercase tracking-widest text-neutral-400">Total</span><span className="font-display text-3xl" data-testid="proposal-total">${Number(p.total).toLocaleString()}</span></div>
        <div className="flex items-center justify-between px-5 py-4"><span className="text-sm text-neutral-400">Deposit to lock the date ({p.deposit_pct}%)</span><span className="font-display text-2xl" data-testid="proposal-deposit">${Number(p.deposit).toLocaleString()}</span></div>
      </div>
      {p.notes && <p className="mt-6 whitespace-pre-line text-sm text-neutral-300">{p.notes}</p>}
      <div className="mt-10 flex flex-wrap gap-3">
        {p.paid ? (
          <span className="btn-chrome flex items-center gap-2 px-7 py-3.5 text-[12px]" data-testid="proposal-paid"><Check className="h-4 w-4" /> Deposit received — date locked</span>
        ) : (
          <button onClick={pay} disabled={busy} data-testid="proposal-pay-btn" className="btn-gold flex items-center gap-2 px-8 py-4 text-[12px] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Pay ${Number(p.deposit).toLocaleString()} deposit
          </button>
        )}
        <a href={mediaSrc(p.proposal_url)} target="_blank" rel="noreferrer" data-testid="proposal-pdf-btn" className="btn-chrome flex items-center gap-2 px-8 py-4 text-[12px]"><Download className="h-4 w-4" /> Proposal PDF</a>
      </div>
      <p className="mt-4 text-[10px] uppercase tracking-widest text-neutral-500">Secure checkout by Stripe · Balance due 30 days before the event</p>
    </div>
  );
}
